/**
 * Complete the Look - Product Block
 * Enterprise-Grade "Weighted Booster" Recommendation Engine
 * High-performance, score-based algorithm with Shopify ML integration
 */

class CompleteLookBlock {
  constructor() {
    this.debug = true; // Enable debug mode to see what's happening
    this.cacheExpiry = 10 * 60 * 1000; // 10 minutes
    this.cache = new Map();
    this.currentLookIndex = 0;
    this.lookRecommendations = [];
    
    // Session storage cache key
    this.CACHE_KEY = 'complete_look_recommendations';
    this.CACHE_TIMESTAMP_KEY = 'complete_look_cache_time';
    
    // Weighted Booster: Vibe Clusters for enhanced matching
    this.vibeClusters = {
      'summer': new Set(['linen', 'shorts', 'sandals', 'lightweight', 'breathable', 'beach']),
      'office': new Set(['blazer', 'formal', 'business', 'professional', 'dress-shirt', 'tie']),
      'casual': new Set(['jeans', 'tshirt', 'sneakers', 'hoodie', 'relaxed', 'everyday']),
      'athletic': new Set(['sports', 'gym', 'workout', 'athletic', 'performance', 'active']),
      'evening': new Set(['party', 'evening', 'formal', 'elegant', 'sophisticated', 'dressy'])
    };
    
    // Enhanced Color Harmony with Earth Tones and Color Families
    this.colorHarmonyEnhanced = {
      'navy': ['white', 'light-blue', 'beige', 'grey', 'cream', 'tan'],
      'black': ['white', 'grey', 'silver', 'red', 'gold', 'cream'],
      'white': ['navy', 'black', 'blue', 'grey', 'any'],
      'grey': ['white', 'black', 'navy', 'blue', 'pink', 'charcoal'],
      'blue': ['white', 'navy', 'beige', 'brown', 'grey', 'tan'],
      'brown': ['beige', 'cream', 'white', 'blue', 'green', 'tan', 'olive'],
      'beige': ['navy', 'brown', 'white', 'blue', 'green', 'olive', 'tan'],
      'olive': ['beige', 'brown', 'tan', 'cream', 'white'],
      'tan': ['navy', 'brown', 'beige', 'olive', 'white'],
      'red': ['black', 'white', 'navy', 'grey', 'beige'],
      'green': ['beige', 'brown', 'white', 'navy', 'olive'],
      'default': ['white', 'black', 'grey', 'navy']
    };
    
    // Preserve all fashion intelligence rules
    this.fashionRules = {
      colorHarmony: {
        'navy': ['white', 'light-blue', 'beige', 'grey', 'cream'],
        'black': ['white', 'grey', 'silver', 'red', 'gold'],
        'white': ['navy', 'black', 'blue', 'grey', 'any'],
        'grey': ['white', 'black', 'navy', 'blue', 'pink'],
        'blue': ['white', 'navy', 'beige', 'brown', 'grey'],
        'brown': ['beige', 'cream', 'white', 'blue', 'green'],
        'beige': ['navy', 'brown', 'white', 'blue', 'green'],
        'red': ['black', 'white', 'navy', 'grey', 'beige'],
        'default': ['white', 'black', 'grey', 'navy']
      },
      
      fabricCompatibility: {
        'cotton': ['cotton', 'linen', 'denim', 'canvas'],
        'silk': ['silk', 'cotton', 'wool', 'cashmere'],
        'wool': ['wool', 'cotton', 'silk', 'cashmere'],
        'denim': ['cotton', 'denim', 'canvas', 'leather'],
        'linen': ['cotton', 'linen', 'silk', 'hemp'],
        'polyester': ['cotton', 'polyester', 'blend'],
        'cashmere': ['wool', 'silk', 'cashmere', 'cotton']
      },
      
      formalityLevels: {
        'casual': 1,
        'smart-casual': 2,
        'business-casual': 3,
        'business-formal': 4,
        'formal': 5
      }
    };
    
    // Category relationships preserved
    this.categoryRelationships = {
      'shirt': {
        primary: ['trouser', 'jeans', 'chinos'],
        secondary: ['blazer', 'jacket', 'tie', 'belt'],
        accessories: ['watch', 'shoes', 'bag'],
        fallback: ['shirt']
      },
      'trouser': {
        primary: ['shirt', 'polo', 'tshirt'],
        secondary: ['blazer', 'jacket', 'belt'],
        accessories: ['shoes', 'watch', 'bag'],
        fallback: ['trouser', 'jeans']
      },
      'jeans': {
        primary: ['tshirt', 'shirt', 'polo'],
        secondary: ['jacket', 'hoodie', 'belt'],
        accessories: ['sneakers', 'boots', 'cap'],
        fallback: ['jeans', 'trouser']
      },
      'blazer': {
        primary: ['shirt', 'trouser'],
        secondary: ['tie', 'pocket-square'],
        accessories: ['shoes', 'watch', 'belt'],
        fallback: ['blazer', 'jacket']
      },
      'dress': {
        primary: ['jacket', 'cardigan'],
        secondary: ['belt', 'scarf'],
        accessories: ['shoes', 'bag', 'jewelry'],
        fallback: ['dress']
      },
      'tshirt': {
        primary: ['jeans', 'shorts', 'chinos'],
        secondary: ['jacket', 'hoodie'],
        accessories: ['sneakers', 'cap', 'backpack'],
        fallback: ['tshirt', 'polo']
      }
    };
    
    this.init();
  }

  init() {
    if (this.debug) console.log('👗 Complete the Look: Initializing...');
    
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeBlocks();
    });
  }

  initializeBlocks() {
    const blocks = document.querySelectorAll('[data-complete-look-block]');
    if (this.debug) console.log('🎯 Found Complete the Look blocks:', blocks.length);
    
    blocks.forEach(block => this.initializeBlock(block));
  }

  async initializeBlock(block) {
    try {
      // Enable debug mode temporarily to see what's happening
      this.debug = true;
      
      const outfitBundles = this.getOutfitBundles(block);
      if (this.debug) console.log('👗 Outfit bundles found:', outfitBundles.length);
      if (this.debug) console.log('👗 Raw bundles data:', block.dataset.outfitBundles);
      
      if (outfitBundles.length === 0) {
        console.log('⚠️ No outfit bundles found in metafields, generating real product recommendations');
        // Generate real outfit recommendations using store products
        const currentProductId = block.dataset.currentProductId;
        const currentProductHandle = block.dataset.currentProductHandle;
        
        if (!currentProductHandle) {
          console.error('❌ No current product handle found');
          this.hideBlock(block);
          return;
        }
        
        const realOutfits = await this.generateRealOutfitBundles(currentProductId, currentProductHandle);
        if (realOutfits.length > 0) {
          this.outfitBundles = realOutfits;
          this.currentOutfitIndex = 0;
          this.currentBlock = block;
          
          await this.renderOutfits(block, realOutfits);
          this.setupNavigation(block);
          
          if (this.debug) console.log('✅ Complete the Look block initialized with real products');
          return;
        }
        
        console.warn('⚠️ No real outfits could be generated');
        this.hideBlock(block);
        return;
      }
      
      // Process bundles to ensure each has exactly 3 products
      const processedOutfits = await this.processOutfitBundles(outfitBundles);
      
      if (processedOutfits.length === 0) {
        console.warn('⚠️ No valid outfits after processing, hiding block');
        this.hideBlock(block);
        return;
      }
      
      this.outfitBundles = processedOutfits;
      this.currentOutfitIndex = 0;
      this.currentBlock = block;
      
      await this.renderOutfits(block, processedOutfits);
      this.setupNavigation(block);
      
      if (this.debug) console.log('✅ Complete the Look block initialized with metafield data');
      
    } catch (error) {
      console.error('❌ Complete the Look failed:', error);
      this.hideBlock(block);
    }
  }

  async generateRealOutfitBundles(currentProductId, currentProductHandle) {
    try {
      if (this.debug) console.log('🎯 Generating real outfit bundles for product:', currentProductHandle);
      
      // Fetch products from your store collections
      const products = await this.fetchStoreProducts();
      if (products.length === 0) {
        console.warn('No products fetched from store');
        return [];
      }
      
      if (this.debug) console.log('📦 Total products fetched:', products.length);
      
      // Create outfit bundles based on product categories
      const outfits = this.createOutfitBundlesFromProducts(products, currentProductHandle);
      
      if (this.debug) console.log('🎯 Generated real outfits:', outfits.length);
      return outfits;
      
    } catch (error) {
      console.error('Failed to generate real outfit bundles:', error);
      return [];
    }
  }

  async fetchStoreProducts() {
    try {
      // Fetch products from multiple collections to get variety
      const collections = ['all', 'shirts', 'trousers', 'jackets', 'accessories', 'featured', 'new-arrivals'];
      const allProducts = [];
      let successfulFetches = 0;
      
      for (const collection of collections) {
        try {
          const response = await fetch(`/collections/${collection}/products.json?limit=20`);
          if (response.ok) {
            const data = await response.json();
            if (data.products && data.products.length > 0) {
              allProducts.push(...data.products.map(p => this.normalizeProductData(p)));
              successfulFetches++;
              if (this.debug) console.log(`✅ Fetched ${data.products.length} products from ${collection}`);
            }
          } else {
            if (this.debug) console.warn(`⚠️ Collection ${collection} returned ${response.status}`);
          }
        } catch (error) {
          if (this.debug) console.warn(`Failed to fetch from collection ${collection}:`, error);
        }
      }
      
      // Remove duplicates based on product ID
      const uniqueProducts = allProducts.filter((product, index, self) => 
        index === self.findIndex(p => p.id === product.id)
      );
      
      if (this.debug) {
        console.log(`📦 Fetched from ${successfulFetches}/${collections.length} collections`);
        console.log('📦 Total unique products:', uniqueProducts.length);
      }
      
      // If we still don't have enough products, try a general search
      if (uniqueProducts.length < 10) {
        if (this.debug) console.log('🔍 Not enough products, trying search fallback...');
        const searchProducts = await this.getProductsFromSearch();
        uniqueProducts.push(...searchProducts.filter(sp => 
          !uniqueProducts.some(up => up.id === sp.id)
        ));
        if (this.debug) console.log('📦 After search fallback:', uniqueProducts.length);
      }
      
      return uniqueProducts;
      
    } catch (error) {
      console.error('Failed to fetch store products:', error);
      return [];
    }
  }

  createOutfitBundlesFromProducts(products, currentProductHandle) {
    const outfits = [];
    
    // Categorize products
    const shirts = products.filter(p => this.isShirt(p));
    const bottoms = products.filter(p => this.isBottom(p));
    const layers = products.filter(p => this.isLayer(p));
    
    if (this.debug) {
      console.log('👔 Shirts found:', shirts.length, shirts.map(s => s.title));
      console.log('👖 Bottoms found:', bottoms.length, bottoms.map(b => b.title));
      console.log('🧥 Layers found:', layers.length, layers.map(l => l.title));
    }
    
    // Ensure we have enough products to create meaningful outfits
    if (shirts.length === 0 && bottoms.length === 0 && layers.length === 0) {
      console.warn('No categorized products found for outfit creation');
      return [];
    }
    
    // Create up to 3 outfit combinations, focusing on shirts as requested
    const maxOutfits = Math.min(3, Math.max(shirts.length, bottoms.length, layers.length));
    
    for (let i = 0; i < maxOutfits; i++) {
      const outfit = [];
      
      // Strategy 1: Start with a shirt (user requested shirts bundle)
      if (shirts.length > i) {
        const shirt = shirts[i];
        if (shirt.handle !== currentProductHandle) {
          outfit.push(shirt);
        }
      }
      
      // Strategy 2: Add a complementary bottom
      if (bottoms.length > i) {
        const bottom = bottoms[i];
        if (bottom.handle !== currentProductHandle && !outfit.some(p => p.id === bottom.id)) {
          outfit.push(bottom);
        }
      }
      
      // Strategy 3: Add a layer/accessory
      if (layers.length > i) {
        const layer = layers[i];
        if (layer.handle !== currentProductHandle && !outfit.some(p => p.id === layer.id)) {
          outfit.push(layer);
        }
      }
      
      // Strategy 4: If we don't have 3 items yet, fill with any available products
      if (outfit.length < 3) {
        const remainingProducts = products.filter(p => 
          p.handle !== currentProductHandle && 
          !outfit.some(op => op.id === p.id)
        );
        
        for (const product of remainingProducts) {
          if (outfit.length >= 3) break;
          outfit.push(product);
        }
      }
      
      // Only add outfit if it has at least 2 products
      if (outfit.length >= 2) {
        outfits.push(outfit);
        if (this.debug) {
          console.log(`✅ Created outfit ${i + 1}:`, outfit.map(p => p.title));
        }
      }
    }
    
    // If we still don't have any outfits, create a fallback with available products
    if (outfits.length === 0 && products.length >= 2) {
      const fallbackOutfit = products
        .filter(p => p.handle !== currentProductHandle)
        .slice(0, 3);
      
      if (fallbackOutfit.length >= 2) {
        outfits.push(fallbackOutfit);
        if (this.debug) {
          console.log('🔄 Created fallback outfit:', fallbackOutfit.map(p => p.title));
        }
      }
    }
    
    return outfits;
  }

  isShirt(product) {
    const title = product.title.toLowerCase();
    const type = (product.type || '').toLowerCase();
    const tags = (product.tags || []).join(' ').toLowerCase();
    const allText = `${title} ${type} ${tags}`;
    
    // Positive indicators for shirts
    const shirtKeywords = [
      'shirt', 'polo', 'tee', 't-shirt', 'blouse', 'top', 'tunic',
      'button-up', 'button-down', 'dress shirt', 'casual shirt'
    ];
    
    // Negative indicators (not shirts)
    const notShirtKeywords = [
      'jacket', 'coat', 'blazer', 'hoodie', 'sweatshirt', 'cardigan',
      'trouser', 'pant', 'jean', 'short', 'skirt', 'dress', 'suit'
    ];
    
    // Check for negative indicators first
    if (notShirtKeywords.some(keyword => allText.includes(keyword))) {
      return false;
    }
    
    // Check for positive indicators
    return shirtKeywords.some(keyword => allText.includes(keyword));
  }

  isBottom(product) {
    const title = product.title.toLowerCase();
    const type = (product.type || '').toLowerCase();
    const tags = (product.tags || []).join(' ').toLowerCase();
    const allText = `${title} ${type} ${tags}`;
    
    // Positive indicators for bottoms
    const bottomKeywords = [
      'trouser', 'pant', 'jean', 'chino', 'short', 'skirt', 'legging',
      'jogger', 'sweatpant', 'cargo', 'formal pant', 'dress pant'
    ];
    
    return bottomKeywords.some(keyword => allText.includes(keyword));
  }

  isLayer(product) {
    const title = product.title.toLowerCase();
    const type = (product.type || '').toLowerCase();
    const tags = (product.tags || []).join(' ').toLowerCase();
    const allText = `${title} ${type} ${tags}`;
    
    // Positive indicators for layers/accessories
    const layerKeywords = [
      'jacket', 'blazer', 'coat', 'cardigan', 'sweater', 'hoodie',
      'vest', 'waistcoat', 'shawl', 'scarf', 'tie', 'belt', 'watch',
      'bag', 'accessory', 'hat', 'cap', 'shoe', 'boot', 'sneaker'
    ];
    
    return layerKeywords.some(keyword => allText.includes(keyword));
  }

  getOutfitBundles(block) {
    const bundlesData = block.dataset.outfitBundles;
    if (this.debug) console.log('📦 Raw bundles data:', bundlesData);
    
    if (!bundlesData || bundlesData === 'null' || bundlesData === '{}') {
      if (this.debug) console.log('📦 No bundles data found');
      return [];
    }
    
    try {
      const metafields = JSON.parse(decodeURIComponent(bundlesData));
      if (this.debug) console.log('📦 Parsed metafields:', metafields);
      
      const bundles = [];
      
      // Handle different possible metafield structures
      if (metafields && typeof metafields === 'object') {
        // Extract bundles from metafields (bundle_1, bundle_2, bundle_3, etc.)
        Object.keys(metafields).forEach(key => {
          if (this.debug) console.log('📦 Processing key:', key, metafields[key]);
          
          if (key.startsWith('bundle_') && metafields[key]) {
            let bundleProducts = null;
            
            // Handle different metafield value structures
            if (metafields[key].value) {
              bundleProducts = metafields[key].value;
            } else if (Array.isArray(metafields[key])) {
              bundleProducts = metafields[key];
            } else {
              bundleProducts = metafields[key];
            }
            
            if (Array.isArray(bundleProducts) && bundleProducts.length > 0) {
              bundles.push(bundleProducts);
              if (this.debug) console.log('📦 Added bundle:', bundleProducts);
            }
          }
        });
      }
      
      if (this.debug) console.log('📦 Final bundles:', bundles);
      return bundles;
    } catch (error) {
      console.warn('Failed to parse outfit bundles:', error);
      return [];
    }
  }

  async processOutfitBundles(bundles) {
    const processedOutfits = [];
    
    for (const bundle of bundles) {
      const outfit = await this.processOutfitBundle(bundle);
      if (outfit && outfit.length >= 2) { // At least 2 products for a meaningful outfit
        processedOutfits.push(outfit);
      }
    }
    
    return processedOutfits;
  }

  async processOutfitBundle(bundle) {
    const outfit = [];
    
    // Fetch product details for each product in the bundle
    for (const productRef of bundle) {
      try {
        let productHandle = '';
        
        // Handle different metafield formats
        if (typeof productRef === 'string') {
          productHandle = productRef;
        } else if (productRef.handle) {
          productHandle = productRef.handle;
        } else if (productRef.id) {
          // If we have product ID, we need to fetch the handle
          continue; // Skip for now, implement ID lookup if needed
        }
        
        if (productHandle) {
          const productData = await this.fetchProductData(productHandle);
          if (productData) {
            outfit.push(productData);
          }
        }
      } catch (error) {
        console.warn('Failed to fetch product:', productRef, error);
      }
    }
    
    // Ensure outfit has exactly 3 products
    return this.ensureOutfitCompleteness(outfit);
  }

  async fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        const product = await response.json();
        return this.normalizeProductData(product);
      }
    } catch (error) {
      console.warn('Failed to fetch product data for:', handle, error);
    }
    return null;
  }

  normalizeProductData(product) {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      price: product.price,
      compare_at_price: product.compare_at_price,
      url: product.url || `/products/${product.handle}`,
      featured_image: product.featured_image,
      images: product.images || [],
      available: product.available,
      type: product.type || '',
      vendor: product.vendor || ''
    };
  }

  ensureOutfitCompleteness(outfit) {
    // Ensure we have exactly 3 products for outfit integrity
    if (outfit.length >= 3) {
      return outfit.slice(0, 3); // Take first 3 products
    }
    
    // If we have fewer than 3, we should ideally fetch fallback products
    // For now, return what we have if it's at least 2 products
    return outfit.length >= 2 ? outfit : [];
  }

  hideBlock(block) {
    block.style.display = 'none';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Preserve all analysis methods from original
  analyzeProduct(product) {
    return {
      category: this.extractCategory(product),
      colorFamily: this.extractColorFamily(product),
      fabric: this.extractFabric(product),
      fit: this.extractFit(product),
      pattern: this.extractPattern(product),
      occasion: this.extractOccasion(product),
      formality: this.extractFormality(product),
      season: this.extractSeason(product),
      priceRange: this.categorizePriceRange(product.price),
      styleProfile: this.extractStyleProfile(product)
    };
  }

  extractCategory(product) {
    const type = (product.type || '').toLowerCase();
    const title = (product.title || '').toLowerCase();
    const tags = (product.tags || []).map(tag => tag.toLowerCase());
    
    const categoryMap = {
      'shirt': ['shirt', 'blouse', 'top'],
      'trouser': ['trouser', 'pant', 'formal-pant'],
      'jeans': ['jeans', 'denim'],
      'tshirt': ['tshirt', 't-shirt', 'tee'],
      'blazer': ['blazer', 'suit-jacket', 'sport-coat'],
      'jacket': ['jacket', 'coat', 'outerwear'],
      'dress': ['dress', 'gown', 'frock'],
      'shorts': ['shorts', 'short'],
      'polo': ['polo', 'polo-shirt'],
      'hoodie': ['hoodie', 'sweatshirt', 'pullover']
    };
    
    for (const [category, synonyms] of Object.entries(categoryMap)) {
      if (synonyms.some(synonym => type.includes(synonym) || title.includes(synonym) || 
          tags.some(tag => tag.includes(synonym)))) {
        return category;
      }
    }
    
    return 'unknown';
  }

  extractColorFamily(product) {
    const title = (product.title || '').toLowerCase();
    const tags = (product.tags || []).map(tag => tag.toLowerCase());
    const description = (product.description || '').toLowerCase();
    const allText = `${title} ${tags.join(' ')} ${description}`;
    const words = allText.split(/\s+/);
    
    for (const word of words) {
      const cleanWord = word.replace(/[^a-z]/g, '');
      if (cleanWord.length >= 3 && cleanWord.length <= 15) {
        const nonColorWords = ['size', 'small', 'medium', 'large', 'cotton', 'silk', 'wool', 'shirt', 'pant', 'dress'];
        if (!nonColorWords.includes(cleanWord)) {
          return cleanWord;
        }
      }
    }
    return 'neutral';
  }

  extractFabric(product) {
    const allText = `${product.title || ''} ${(product.tags || []).join(' ')} ${product.description || ''}`.toLowerCase();
    const fabricKeywords = {
      'cotton': ['cotton', '100% cotton', 'organic cotton'],
      'silk': ['silk', 'mulberry silk', 'pure silk'],
      'wool': ['wool', 'merino', 'lambswool'],
      'denim': ['denim', 'jean', 'chambray'],
      'linen': ['linen', 'flax', 'hemp'],
      'polyester': ['polyester', 'poly', 'synthetic'],
      'cashmere': ['cashmere', 'pashmina'],
      'leather': ['leather', 'genuine leather'],
      'blend': ['blend', 'mix', 'poly-cotton']
    };
    
    for (const [fabric, keywords] of Object.entries(fabricKeywords)) {
      if (keywords.some(keyword => allText.includes(keyword))) {
        return fabric;
      }
    }
    return 'unknown';
  }

  extractFit(product) {
    const allText = `${product.title || ''} ${(product.tags || []).join(' ')}`.toLowerCase();
    const fitKeywords = {
      'slim': ['slim', 'fitted', 'tailored', 'narrow'],
      'regular': ['regular', 'classic', 'standard'],
      'relaxed': ['relaxed', 'loose', 'comfortable', 'easy'],
      'oversized': ['oversized', 'baggy', 'loose-fit']
    };
    
    for (const [fit, keywords] of Object.entries(fitKeywords)) {
      if (keywords.some(keyword => allText.includes(keyword))) {
        return fit;
      }
    }
    return 'regular';
  }

  extractPattern(product) {
    const allText = `${product.title || ''} ${(product.tags || []).join(' ')}`.toLowerCase();
    const patternKeywords = {
      'solid': ['solid', 'plain', 'uni'],
      'striped': ['striped', 'stripe', 'pinstripe'],
      'checked': ['checked', 'check', 'plaid', 'gingham'],
      'printed': ['printed', 'print', 'graphic', 'floral'],
      'textured': ['textured', 'texture', 'woven', 'knit']
    };
    
    for (const [pattern, keywords] of Object.entries(patternKeywords)) {
      if (keywords.some(keyword => allText.includes(keyword))) {
        return pattern;
      }
    }
    return 'solid';
  }

  extractOccasion(product) {
    const allText = `${product.title || ''} ${(product.tags || []).join(' ')} ${product.description || ''}`.toLowerCase();
    const occasionKeywords = {
      'formal': ['formal', 'business', 'office', 'professional'],
      'casual': ['casual', 'everyday', 'weekend', 'relaxed'],
      'party': ['party', 'evening', 'night', 'celebration'],
      'sports': ['sports', 'gym', 'athletic', 'workout'],
      'travel': ['travel', 'vacation', 'holiday']
    };
    
    for (const [occasion, keywords] of Object.entries(occasionKeywords)) {
      if (keywords.some(keyword => allText.includes(keyword))) {
        return occasion;
      }
    }
    return 'casual';
  }

  extractFormality(product) {
    const occasion = this.extractOccasion(product);
    const category = this.extractCategory(product);
    let formality = this.fashionRules.formalityLevels[occasion] || 2;
    
    const categoryAdjustments = {
      'blazer': +1, 'suit': +2, 'dress-shirt': +1,
      'tshirt': -1, 'hoodie': -2, 'jeans': -1
    };
    
    formality += categoryAdjustments[category] || 0;
    return Math.max(1, Math.min(5, formality));
  }

  extractSeason(product) {
    const fabric = this.extractFabric(product);
    const tags = (product.tags || []).map(tag => tag.toLowerCase());
    
    const seasonKeywords = {
      'spring': ['spring', 'ss', 'spring-summer'],
      'summer': ['summer', 'ss', 'spring-summer'],
      'autumn': ['autumn', 'fall', 'aw', 'autumn-winter'],
      'winter': ['winter', 'aw', 'autumn-winter']
    };
    
    for (const [season, keywords] of Object.entries(seasonKeywords)) {
      if (tags.some(tag => keywords.some(keyword => tag.includes(keyword)))) {
        return season;
      }
    }
    
    const fabricSeasons = {
      'linen': 'summer', 'cotton': 'spring', 'wool': 'winter',
      'cashmere': 'winter', 'silk': 'spring'
    };
    
    return fabricSeasons[fabric] || 'all-season';
  }

  categorizePriceRange(price) {
    if (price < 1000) return 'budget';
    if (price < 3000) return 'mid-range';
    if (price < 6000) return 'premium';
    return 'luxury';
  }

  extractStyleProfile(product) {
    const formality = this.extractFormality(product);
    const occasion = this.extractOccasion(product);
    const category = this.extractCategory(product);
    
    if (formality >= 4) return 'formal';
    if (formality <= 2) return 'casual';
    if (['blazer', 'chinos', 'polo'].includes(category)) return 'smart-casual';
    if (occasion === 'sports') return 'athletic';
    
    return 'casual';
  }

  // Generate recommendations with Weighted Booster system
  async generateLookRecommendations(analysis, config) {
    try {
      const candidateProducts = await this.getCandidateProducts(analysis, config);
      console.log(`🔍 Found ${candidateProducts.length} candidate products`);
      
      if (candidateProducts.length === 0) return [];
      
      const scoredProducts = this.scoreProductsForLook(candidateProducts, analysis, config.currentProduct);
      console.log(`📊 Scored ${scoredProducts.length} products (Weighted Booster)`);
      
      // Log top 3 scores for debugging
      if (this.debug && scoredProducts.length > 0) {
        console.log('� Top 3 Scores:', scoredProducts.slice(0, 3).map(p => ({
          title: p.title,
          weightedScore: p.weightedScore?.toFixed(1),
          finalScore: p.compatibilityScore?.toFixed(1)
        })));
      }
      
      const lookCombinations = this.createLookCombinations(scoredProducts, analysis, config);
      console.log(`🎨 Created ${lookCombinations.length} look combinations`);
      
      const bestLooks = this.selectBestLooks(lookCombinations, 5); // Max 5 for carousel
      console.log(`⭐ Selected ${bestLooks.length} best looks`);
      
      return bestLooks;
    } catch (error) {
      console.warn('Failed to generate look recommendations:', error);
      return [];
    }
  }

  // WEIGHTED BOOSTER: High-Performance Data Fetching with Shopify ML
  async getCandidateProducts(analysis, config) {
    const startTime = performance.now();
    
    // Check session storage cache first (aggressive caching)
    const cachedData = this.getCachedRecommendations(config.currentProduct.id);
    if (cachedData) {
      console.log(`⚡ Loaded ${cachedData.length} products from cache (${(performance.now() - startTime).toFixed(2)}ms)`);
      return cachedData;
    }
    
    const candidates = new Set();
    
    try {
      // PRIMARY SOURCE: Shopify's native Recommendations API (ML-powered)
      const shopifyRecommendations = await this.getShopifyRecommendations(config.currentProduct.id);
      
      if (shopifyRecommendations.length > 0) {
        console.log(`🤖 Shopify ML returned ${shopifyRecommendations.length} recommendations`);
        shopifyRecommendations.forEach(product => {
          if (product.id !== config.currentProduct.id) {
            product.isShopifyRecommended = true; // Flag for base score boost
            candidates.add(product);
          }
        });
      }
      
      // FALLBACK: Only if Shopify API returns < 2 results
      if (candidates.size < 2) {
        console.log('⚠️ Shopify API returned < 2 results, using fallback...');
        
        // Targeted collection fetch (current product's collection only)
        if (config.currentProduct.collection) {
          const collectionProducts = await this.getProductsFromTargetedCollection(config.currentProduct.collection);
          collectionProducts.forEach(product => {
            if (product.id !== config.currentProduct.id) {
              candidates.add(product);
            }
          });
        }
        
        // If still not enough, use existing multi-source fetch (preserved logic)
        if (candidates.size < 3) {
          const fallbackProducts = await this.getFallbackProducts();
          fallbackProducts.forEach(product => {
            if (product.id !== config.currentProduct.id) {
              candidates.add(product);
            }
          });
        }
      }
      
      const candidatesArray = Array.from(candidates);
      
      // Cache the results in session storage
      this.cacheRecommendations(config.currentProduct.id, candidatesArray);
      
      const fetchTime = (performance.now() - startTime).toFixed(2);
      console.log(`⚡ Fetched ${candidatesArray.length} candidates in ${fetchTime}ms`);
      
      return candidatesArray;
      
    } catch (error) {
      console.warn('Error getting candidate products:', error);
      return [];
    }
  }

  // NEW: Shopify Recommendations API (Primary Source)
  async getShopifyRecommendations(productId) {
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10`);
      
      if (!response.ok) {
        console.warn('Shopify Recommendations API failed:', response.status);
        return [];
      }
      
      const data = await response.json();
      const products = data.products || [];
      
      return products.map(product => this.normalizeProductData(product)).filter(Boolean);
      
    } catch (error) {
      console.warn('Failed to fetch Shopify recommendations:', error);
      return [];
    }
  }

  // NEW: Targeted Collection Fetch (Fallback)
  async getProductsFromTargetedCollection(collectionHandle) {
    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=20`);
      
      if (!response.ok) return [];
      
      const data = await response.json();
      return data.products.map(product => this.normalizeProductData(product)).filter(Boolean);
      
    } catch (error) {
      console.warn(`Failed to fetch from collection ${collectionHandle}:`, error);
      return [];
    }
  }

  // NEW: Session Storage Caching
  getCachedRecommendations(productId) {
    try {
      const cacheKey = `${this.CACHE_KEY}_${productId}`;
      const timestampKey = `${this.CACHE_TIMESTAMP_KEY}_${productId}`;
      
      const cached = sessionStorage.getItem(cacheKey);
      const timestamp = sessionStorage.getItem(timestampKey);
      
      if (!cached || !timestamp) return null;
      
      const age = Date.now() - parseInt(timestamp);
      if (age > this.cacheExpiry) {
        // Cache expired
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(timestampKey);
        return null;
      }
      
      return JSON.parse(cached);
      
    } catch (error) {
      console.warn('Cache read error:', error);
      return null;
    }
  }

  cacheRecommendations(productId, products) {
    try {
      const cacheKey = `${this.CACHE_KEY}_${productId}`;
      const timestampKey = `${this.CACHE_TIMESTAMP_KEY}_${productId}`;
      
      sessionStorage.setItem(cacheKey, JSON.stringify(products));
      sessionStorage.setItem(timestampKey, Date.now().toString());
      
    } catch (error) {
      console.warn('Cache write error:', error);
    }
  }

  // PRESERVED: Fallback multi-source fetch (only used if needed)
  async getFallbackProducts() {
    const products = [];
    
    try {
      // Use Promise.all for parallel requests (performance optimization)
      const [collectionProducts, searchProducts] = await Promise.all([
        this.getProductsFromCollections(),
        this.getProductsFromSearch()
      ]);
      
      products.push(...collectionProducts, ...searchProducts);
      
    } catch (error) {
      console.warn('Fallback fetch error:', error);
    }
    
    return products;
  }

  async getProductsFromCollections() {
    const products = [];
    const collections = ['all', 'shirts', 'featured', 'new-arrivals'];
    
    for (const collectionHandle of collections) {
      try {
        const response = await fetch(`/collections/${collectionHandle}/products.json?limit=50`);
        if (response.ok) {
          const data = await response.json();
          data.products.forEach(product => {
            const normalizedProduct = this.normalizeProductData(product);
            if (normalizedProduct) products.push(normalizedProduct);
          });
        }
      } catch (error) {
        if (this.debug) console.warn(`Failed to fetch collection ${collectionHandle}:`, error);
      }
    }
    
    return products;
  }

  async getProductsFromSearch() {
    const products = [];
    const searchTerms = ['shirt', 'top', 'clothing', 'apparel'];
    
    for (const term of searchTerms) {
      try {
        const response = await fetch(`/search/suggest.json?q=${term}&resources[type]=product&resources[limit]=20`);
        if (response.ok) {
          const data = await response.json();
          if (data.resources?.results?.products) {
            for (const searchProduct of data.resources.results.products) {
              if (searchProduct.handle) {
                try {
                  const productResponse = await fetch(`/products/${searchProduct.handle}.js`);
                  if (productResponse.ok) {
                    const completeProduct = await productResponse.json();
                    const normalizedProduct = this.normalizeProductData(completeProduct);
                    if (normalizedProduct) products.push(normalizedProduct);
                  }
                } catch (productError) {
                  const normalizedProduct = this.normalizeProductData(searchProduct);
                  if (normalizedProduct) products.push(normalizedProduct);
                }
              }
            }
          }
        }
      } catch (error) {
        if (this.debug) console.warn(`Search failed for term ${term}:`, error);
      }
    }
    
    return products;
  }

  normalizeProductData(product) {
    if (!product || !product.id) return null;

    let price = 0;
    if (product.variants && product.variants.length > 0) {
      price = product.variants[0].price || 0;
    } else if (product.price_min) {
      price = product.price_min;
    } else if (product.price) {
      price = product.price;
    }

    if (typeof price === 'string') {
      price = parseFloat(price.replace(/[^\d.]/g, '')) || 0;
    }

    let images = [];
    let featuredImage = '';

    if (product.featured_image) {
      featuredImage = this.normalizeImageUrl(product.featured_image);
      images.push(featuredImage);
    }

    if (product.images && Array.isArray(product.images)) {
      product.images.forEach(img => {
        const normalizedImg = this.normalizeImageUrl(img);
        if (normalizedImg && !images.includes(normalizedImg)) {
          images.push(normalizedImg);
        }
      });
    }

    if (!featuredImage && images.length > 0) {
      featuredImage = images[0];
    }

    let tags = [];
    if (Array.isArray(product.tags)) {
      tags = product.tags;
    } else if (typeof product.tags === 'string') {
      tags = product.tags.split(',').map(tag => tag.trim()).filter(Boolean);
    }

    return {
      id: product.id,
      handle: product.handle || '',
      title: product.title || 'Untitled Product',
      type: product.product_type || product.type || '',
      vendor: product.vendor || '',
      tags: tags,
      price: price,
      compare_at_price: product.compare_at_price || null,
      images: images,
      featured_image: featuredImage,
      available: product.available !== false,
      description: product.body_html || product.description || '',
      url: `/products/${product.handle}`,
      variants: this.normalizeVariants(product)
    };
  }

  normalizeImageUrl(img) {
    if (!img) return '';
    
    let url = '';
    if (typeof img === 'string') {
      url = img.trim();
    } else if (img && typeof img === 'object') {
      url = img.src || img.url || img.original_src || '';
    }
    
    if (!url || url === 'undefined' || url === 'null') return '';
    
    url = String(url).trim();
    
    if (url.startsWith('//')) {
      url = `https:${url}`;
    } else if (url.startsWith('/')) {
      url = `${window.location.origin}${url}`;
    } else if (!url.startsWith('http')) {
      url = `${window.location.origin}/${url}`;
    }
    
    try {
      new URL(url);
      return url;
    } catch (error) {
      if (this.debug) console.warn('Invalid image URL:', url);
      return '';
    }
  }

  normalizeVariants(product) {
    if (!product) return [];
    
    if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
      const normalizedVariants = product.variants.map(variant => {
        const variantId = variant.id || variant.variant_id;
        if (!variantId || isNaN(variantId)) return null;
        
        return {
          id: parseInt(variantId),
          title: variant.title || 'Default Title',
          price: variant.price || product.price || 0,
          available: variant.available !== false,
          inventory_quantity: variant.inventory_quantity || 0,
          sku: variant.sku || '',
          option1: variant.option1 || null,
          option2: variant.option2 || null,
          option3: variant.option3 || null
        };
      }).filter(Boolean);
      
      if (normalizedVariants.length > 0) return normalizedVariants;
    }
    
    let variantId = null;
    if (product.id && !isNaN(product.id)) {
      variantId = parseInt(product.id);
    } else if (product.variant_id && !isNaN(product.variant_id)) {
      variantId = parseInt(product.variant_id);
    } else if (product.variants && product.variants[0] && product.variants[0].id) {
      variantId = parseInt(product.variants[0].id);
    }
    
    if (!variantId || isNaN(variantId)) return [];
    
    return [{
      id: variantId,
      title: 'Default Title',
      price: product.price || 0,
      available: product.available !== false,
      inventory_quantity: 1,
      sku: product.sku || '',
      option1: null,
      option2: null,
      option3: null
    }];
  }

  scoreProductsForLook(candidates, currentAnalysis, currentProduct) {
    const validProducts = candidates.filter(product => {
      const validation = this.validateProductForCart(product);
      if (!validation.valid && this.debug) {
        console.warn(`⚠️ Excluding ${product.title}: ${validation.reason}`);
      }
      return validation.valid;
    });
    
    const scoredProducts = validProducts.map(product => {
      const productAnalysis = this.analyzeProduct(product);
      
      // Use NEW Weighted Booster scoring
      const weightedScore = this.calculateWeightedScore(
        currentProduct,
        currentAnalysis,
        product,
        productAnalysis
      );
      
      // Also calculate legacy compatibility score (preserved)
      const legacyScore = this.calculateLegacyCompatibilityScore(currentAnalysis, productAnalysis);
      
      // Combine scores: 70% weighted, 30% legacy (for smooth transition)
      const finalScore = (weightedScore * 0.7) + (legacyScore * 100 * 0.3);
      
      return {
        ...product,
        analysis: productAnalysis,
        compatibilityScore: finalScore,
        weightedScore: weightedScore,
        legacyScore: legacyScore,
        reasons: this.getCompatibilityReasons(currentAnalysis, productAnalysis)
      };
    });
    
    // Sort by final score DESC
    return scoredProducts.sort((a, b) => b.compatibilityScore - a.compatibilityScore);
  }

  // PRESERVED: Legacy compatibility scoring (renamed for clarity)
  calculateLegacyCompatibilityScore(current, candidate) {
    let score = 0;
    score += this.getCategoryCompatibility(current.category, candidate.category) * 0.4;
    score += this.getColorCompatibility(current.colorFamily, candidate.colorFamily) * 0.25;
    score += this.getFormalityCompatibility(current.formality, candidate.formality) * 0.15;
    score += this.getFabricCompatibility(current.fabric, candidate.fabric) * 0.1;
    score += this.getPatternCompatibility(current.pattern, candidate.pattern) * 0.05;
    score += this.getPriceCompatibility(current.priceRange, candidate.priceRange) * 0.05;
    return score;
  }

  validateProductForCart(product) {
    if (!product) return { valid: false, reason: 'Product is null' };
    if (!product.id) return { valid: false, reason: 'Product has no ID' };
    if (!product.variants || product.variants.length === 0) return { valid: false, reason: 'Product has no variants' };
    
    const validVariants = product.variants.filter(v => v.id && !isNaN(v.id));
    if (validVariants.length === 0) return { valid: false, reason: 'No variants with valid IDs' };
    
    const availableVariants = validVariants.filter(v => v.available);
    if (availableVariants.length === 0) return { valid: false, reason: 'No available variants' };
    
    return { valid: true, reason: 'Product is valid' };
  }

  // WEIGHTED BOOSTER: Enterprise-Grade Scoring Algorithm (0-100 Scale)
  calculateWeightedScore(mainProduct, mainAnalysis, candidateProduct, candidateAnalysis) {
    let score = 0;
    
    // BASE SCORE: +50 if from Shopify Recommendations API
    if (candidateProduct.isShopifyRecommended) {
      score += 50;
    } else {
      score += 30; // Lower base for fallback sources
    }
    
    // BOOSTER 1: Price Anchoring (+20 points)
    // Psychology: Sweet spot is 20-60% of main item price
    const mainPrice = this.getNumericPrice(mainProduct);
    const candidatePrice = this.getNumericPrice(candidateProduct);
    
    if (mainPrice > 0 && candidatePrice > 0) {
      const priceRatio = candidatePrice / mainPrice;
      
      if (priceRatio >= 0.2 && priceRatio <= 0.6) {
        score += 20; // Perfect add-on price range
      } else if (priceRatio > 0.6 && priceRatio <= 0.9) {
        score += 10; // Good price range
      } else if (priceRatio > 1.0) {
        score -= 20; // Penalty: More expensive than main item
      }
    }
    
    // BOOSTER 2: Enhanced Color Harmony (+15 points)
    const colorScore = this.getEnhancedColorScore(mainAnalysis.colorFamily, candidateAnalysis.colorFamily);
    score += colorScore * 15;
    
    // BOOSTER 3: Vibe Match (+15 points)
    // Check if products share a "Vibe Cluster"
    const vibeScore = this.getVibeMatchScore(mainProduct, candidateProduct);
    score += vibeScore * 15;
    
    // BOOSTER 4: Silhouette Balance (+10 points)
    // Visual balance: Oversized + Slim = Perfect
    if (this.hasSilhouetteBalance(mainAnalysis.fit, candidateAnalysis.fit)) {
      score += 10;
    }
    
    // BOOSTER 5: Category Complementarity (from existing logic)
    const categoryScore = this.getCategoryCompatibility(mainAnalysis.category, candidateAnalysis.category);
    score += categoryScore * 10;
    
    // PENALTY 1: Category Clash (-100 points)
    // Same category = instant disqualification
    if (mainAnalysis.category === candidateAnalysis.category && mainAnalysis.category !== 'unknown') {
      score -= 100;
    }
    
    // PENALTY 2: Pattern Clash (-30 points)
    // Rule of "One Hero": Two non-solid patterns clash
    if (mainAnalysis.pattern !== 'solid' && candidateAnalysis.pattern !== 'solid') {
      score -= 30;
    }
    
    // PENALTY 3: Formality Mismatch (-15 points)
    const formalityDiff = Math.abs(mainAnalysis.formality - candidateAnalysis.formality);
    if (formalityDiff > 2) {
      score -= 15;
    }
    
    // Ensure score is within 0-100 range
    return Math.max(0, Math.min(100, score));
  }

  // Helper: Get numeric price for calculations
  getNumericPrice(product) {
    if (product.variants && product.variants.length > 0) {
      return parseFloat(product.variants[0].price) || 0;
    }
    return parseFloat(product.price) || 0;
  }

  // Helper: Enhanced color harmony with earth tones
  getEnhancedColorScore(mainColor, candidateColor) {
    const harmonious = this.colorHarmonyEnhanced[mainColor] || this.colorHarmonyEnhanced['default'] || [];
    
    if (mainColor === candidateColor) return 0.7; // Same color is good
    if (harmonious.includes(candidateColor)) return 1.0; // Perfect harmony
    if (harmonious.includes('any')) return 0.9;
    
    // Check if both are earth tones
    const earthTones = new Set(['brown', 'beige', 'olive', 'tan', 'cream']);
    if (earthTones.has(mainColor) && earthTones.has(candidateColor)) {
      return 0.8; // Earth tones work together
    }
    
    // Neutral colors work with most
    const neutrals = new Set(['white', 'black', 'grey', 'navy']);
    if (neutrals.has(candidateColor) || neutrals.has(mainColor)) {
      return 0.6;
    }
    
    return 0.3; // Unknown combination
  }

  // Helper: Vibe cluster matching
  getVibeMatchScore(mainProduct, candidateProduct) {
    const mainTags = new Set((mainProduct.tags || []).map(t => t.toLowerCase()));
    const candidateTags = new Set((candidateProduct.tags || []).map(t => t.toLowerCase()));
    
    // Check each vibe cluster
    for (const [vibe, keywords] of Object.entries(this.vibeClusters)) {
      let mainMatches = 0;
      let candidateMatches = 0;
      
      keywords.forEach(keyword => {
        if (mainTags.has(keyword)) mainMatches++;
        if (candidateTags.has(keyword)) candidateMatches++;
      });
      
      // If both products match the same vibe cluster
      if (mainMatches > 0 && candidateMatches > 0) {
        return 1.0; // Perfect vibe match
      }
    }
    
    return 0.3; // No strong vibe match
  }

  // Helper: Silhouette balance check
  hasSilhouetteBalance(mainFit, candidateFit) {
    const balancePairs = [
      ['oversized', 'slim'],
      ['oversized', 'fitted'],
      ['relaxed', 'slim'],
      ['loose', 'fitted']
    ];
    
    for (const [fit1, fit2] of balancePairs) {
      if ((mainFit === fit1 && candidateFit === fit2) || 
          (mainFit === fit2 && candidateFit === fit1)) {
        return true;
      }
    }
    
    return false;
  }

  getCategoryCompatibility(currentCategory, candidateCategory) {
    const relationships = this.categoryRelationships[currentCategory];
    if (!relationships) return 0;
    
    if (relationships.primary.includes(candidateCategory)) return 1.0;
    if (relationships.secondary.includes(candidateCategory)) return 0.8;
    if (relationships.accessories.includes(candidateCategory)) return 0.6;
    if (relationships.fallback.includes(candidateCategory)) return 0.4;
    return 0;
  }

  getColorCompatibility(currentColor, candidateColor) {
    const harmonious = this.fashionRules.colorHarmony[currentColor] || this.fashionRules.colorHarmony['default'] || [];
    
    if (currentColor === candidateColor) return 0.8;
    if (harmonious.includes(candidateColor)) return 1.0;
    if (harmonious.includes('any')) return 0.9;
    
    const neutralColors = ['white', 'black', 'grey', 'navy', 'beige'];
    if (neutralColors.includes(candidateColor) || neutralColors.includes(currentColor)) return 0.7;
    
    return 0.5;
  }

  getFormalityCompatibility(currentFormality, candidateFormality) {
    const difference = Math.abs(currentFormality - candidateFormality);
    if (difference === 0) return 1.0;
    if (difference === 1) return 0.8;
    if (difference === 2) return 0.5;
    return 0.2;
  }

  getFabricCompatibility(currentFabric, candidateFabric) {
    const compatible = this.fashionRules.fabricCompatibility[currentFabric] || [];
    if (compatible.includes(candidateFabric)) return 1.0;
    if (currentFabric === candidateFabric) return 0.9;
    return 0.4;
  }

  getPatternCompatibility(currentPattern, candidatePattern) {
    const patternMixing = {
      'solid': ['solid', 'striped', 'checked', 'printed', 'textured'],
      'striped': ['solid', 'checked'],
      'checked': ['solid', 'striped'],
      'printed': ['solid'],
      'textured': ['solid', 'smooth']
    };
    const mixable = patternMixing[currentPattern] || [];
    if (mixable.includes(candidatePattern)) return 1.0;
    return 0.3;
  }

  getPriceCompatibility(currentRange, candidateRange) {
    const rangeOrder = ['budget', 'mid-range', 'premium', 'luxury'];
    const currentIndex = rangeOrder.indexOf(currentRange);
    const candidateIndex = rangeOrder.indexOf(candidateRange);
    const difference = Math.abs(currentIndex - candidateIndex);
    
    if (difference === 0) return 1.0;
    if (difference === 1) return 0.7;
    return 0.4;
  }

  getCompatibilityReasons(current, candidate) {
    const reasons = [];
    
    const relationships = this.categoryRelationships[current.category];
    if (relationships?.primary.includes(candidate.category)) {
      reasons.push('Perfect style match');
    }
    
    const harmonious = this.fashionRules.colorHarmony[current.colorFamily] || this.fashionRules.colorHarmony['default'] || [];
    if (harmonious.includes(candidate.colorFamily)) {
      reasons.push('Harmonious colors');
    } else if (current.colorFamily === candidate.colorFamily) {
      reasons.push('Matching colors');
    }
    
    if (Math.abs(current.formality - candidate.formality) <= 1) {
      reasons.push('Matching formality');
    }
    
    return reasons;
  }

  createLookCombinations(scoredProducts, analysis, config) {
    const combinations = [];
    const currentCategory = analysis.category;
    const relationships = this.categoryRelationships[currentCategory];
    
    const productsByCategory = {};
    scoredProducts.forEach(product => {
      const category = product.analysis.category;
      if (!productsByCategory[category]) productsByCategory[category] = [];
      productsByCategory[category].push(product);
    });
    
    // Strategy 1: Get complementary products from primary categories
    if (relationships && relationships.primary) {
      relationships.primary.forEach(primaryCategory => {
        const primaryProducts = productsByCategory[primaryCategory] || [];
        // Take up to 5 products per primary category to ensure variety
        primaryProducts.slice(0, 5).forEach(primaryProduct => {
          combinations.push({
            type: 'complementary',
            primary: primaryProduct,
            totalScore: primaryProduct.compatibilityScore,
            styleReason: `Perfect match for ${currentCategory}`
          });
        });
      });
    }
    
    // Strategy 2: If we don't have enough, add from secondary categories
    if (combinations.length < 4 && relationships && relationships.secondary) {
      relationships.secondary.forEach(secondaryCategory => {
        const secondaryProducts = productsByCategory[secondaryCategory] || [];
        secondaryProducts.slice(0, 3).forEach(secondaryProduct => {
          combinations.push({
            type: 'complementary',
            primary: secondaryProduct,
            totalScore: secondaryProduct.compatibilityScore * 0.9, // Slightly lower score
            styleReason: `Great addition to ${currentCategory}`
          });
        });
      });
    }
    
    // Strategy 3: If still not enough, add any high-scoring products
    if (combinations.length < 4) {
      scoredProducts.slice(0, 6).forEach(product => {
        // Check if not already added
        const alreadyAdded = combinations.some(c => c.primary && c.primary.id === product.id);
        if (!alreadyAdded) {
          combinations.push({
            type: 'complementary',
            primary: product,
            totalScore: product.compatibilityScore * 0.8,
            styleReason: `Recommended for you`
          });
        }
      });
    }
    
    if (this.debug) {
      console.log(`📊 Created ${combinations.length} look combinations`);
    }
    
    return combinations;
  }

  selectBestLooks(combinations, maxItems) {
    const sorted = combinations.sort((a, b) => b.totalScore - a.totalScore);
    const selected = [];
    const usedProducts = new Set();
    
    for (const combination of sorted) {
      if (selected.length >= maxItems) break;
      if (!combination.primary || !usedProducts.has(combination.primary.id)) {
        selected.push(combination);
        if (combination.primary) usedProducts.add(combination.primary.id);
      }
    }
    
    return selected;
  }

  // Render complete outfit bundles (not individual product recommendations)
  async renderOutfits(block, outfitBundles) {
    const carousel = block.querySelector('[data-look-carousel]');
    if (!carousel) return;
    
    carousel.innerHTML = '';
    
    outfitBundles.forEach((outfit, outfitIndex) => {
      const outfitElement = this.createOutfitElement(outfit, outfitIndex);
      carousel.appendChild(outfitElement);
    });
    
    // Update pagination
    this.updatePagination(block, outfitBundles.length);
    
    // Show first outfit
    this.showOutfitGroup(0);
  }

  // Create HTML for one complete outfit (editorial list layout)
  createOutfitElement(outfit, outfitIndex) {
    const outfitElement = document.createElement('div');
    outfitElement.className = 'look-outfit-group';
    outfitElement.dataset.outfitIndex = outfitIndex;
    
    const productsList = document.createElement('div');
    productsList.className = 'look-products-list';
    
    // Ensure we display exactly 3 products for outfit integrity
    const productsToShow = this.ensureThreeProducts(outfit);
    
    productsToShow.forEach((product, productIndex) => {
      const productItem = this.createOutfitProductItem(product, outfitIndex, productIndex);
      productsList.appendChild(productItem);
    });
    
    outfitElement.appendChild(productsList);
    return outfitElement;
  }

  // Ensure outfit has exactly 3 products (shirt + bottom + layer/accessory)
  ensureThreeProducts(outfit) {
    const products = [...outfit];
    
    // If we have fewer than 3 products, add placeholder or fallback
    while (products.length < 3) {
      products.push(this.createPlaceholderProduct(products.length));
    }
    
    // If we have more than 3, take the first 3
    return products.slice(0, 3);
  }

  createPlaceholderProduct(index) {
    const placeholderTypes = ['Complementary Piece', 'Styling Layer', 'Finishing Touch'];
    return {
      id: `placeholder-${index}`,
      handle: '',
      title: placeholderTypes[index] || 'Complete the Look',
      price: 0,
      url: '#',
      featured_image: null,
      available: false,
      isPlaceholder: true
    };
  }

  // Create individual product item in editorial list format (image left, details right)
  createOutfitProductItem(product, outfitIndex, productIndex) {
    const item = document.createElement('div');
    item.className = 'look-product-item';
    item.dataset.outfitIndex = outfitIndex;
    item.dataset.productIndex = productIndex;
    
    if (product.isPlaceholder) {
      item.classList.add('look-product-item--placeholder');
    }
    
    const imageUrl = this.getProductImage(product);
    const price = this.getShopifyPrice(product);
    
    // Two-column layout: Image LEFT, Details RIGHT
    item.innerHTML = `
      <div class="look-product-image">
        ${imageUrl ? `<a href="${product.url}" aria-label="${this.escapeHtml(product.title)}"><img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" /></a>` :
          '<div class="look-product-placeholder-image"></div>'}
      </div>
      <div class="look-product-details">
        <h4 class="look-product-title">
          ${product.isPlaceholder ? 
            `<span>${this.escapeHtml(product.title)}</span>` :
            `<a href="${product.url}">${this.escapeHtml(product.title)}</a>`
          }
        </h4>
        ${!product.isPlaceholder && price > 0 ? 
          `<div class="look-product-price">₹${this.formatPrice(price)}</div>` : 
          '<div class="look-product-price look-product-price--placeholder">Coming Soon</div>'
        }
        ${!product.isPlaceholder && product.available ? `
          <div class="look-product-actions">
            <button type="button" class="ol-pc-qv"
                    data-product-id="${product.id}"
                    data-product-title="${this.escapeHtml(product.title)}"
                    data-product-price="₹${this.formatPrice(price)}"
                    data-product-url="${product.url}"
                    aria-label="Quick View ${this.escapeHtml(product.title)}">Quick View</button>
          </div>
        ` : ''}
      </div>
    `;
    
    return item;
  }

  getProductImage(product) {
    if (product.isPlaceholder) return null;
    
    // Use the improved image handling method
    return this.getShopifyImage(product);
  }

  // Check if product has multiple variants (size options)
  hasVariants(product) {
    if (!product || !product.variants) return false;
    
    // If product has more than one variant, or if the single variant has options other than "Default Title"
    if (product.variants.length > 1) return true;
    
    if (product.variants.length === 1) {
      const variant = product.variants[0];
      return variant.title !== 'Default Title' && 
             (variant.option1 || variant.option2 || variant.option3);
    }
    
    return false;
  }

  // Get default variant ID for products without size options
  getDefaultVariantId(product) {
    if (!product || !product.variants || product.variants.length === 0) return null;
    
    // Return the first available variant ID
    const availableVariant = product.variants.find(v => v.available && v.id) || product.variants[0];
    return availableVariant ? availableVariant.id : null;
  }

  formatPrice(price) {
    if (!price || price === 0) return '0.00';
    
    // Handle different price formats from Shopify API
    let numPrice = parseFloat(price) || 0;
    
    // Smart price detection: if price is very large (>= 10000), it's likely in cents/paise
    if (numPrice >= 10000) {
      numPrice = numPrice / 100;
    }
    
    return numPrice.toFixed(2);
  }

  // Update pagination display
  updatePagination(block, totalOutfits) {
    const currentPageEl = block.querySelector('.current-page');
    const totalPagesEl = block.querySelector('.total-pages');
    
    if (currentPageEl) currentPageEl.textContent = '1';
    if (totalPagesEl) totalPagesEl.textContent = totalOutfits.toString();
    
    this.totalOutfits = totalOutfits;
  }

  // Show specific outfit group
  showOutfitGroup(outfitIndex) {
    const groups = document.querySelectorAll('.look-outfit-group');
    
    groups.forEach((group, index) => {
      if (index === outfitIndex) {
        group.classList.add('active');
      } else {
        group.classList.remove('active');
      }
    });
    
    this.currentOutfitIndex = outfitIndex;
  }

  // Navigation setup for outfit bundles
  setupNavigation(block) {
    const prevBtn = block.querySelector('.look-nav-prev');
    const nextBtn = block.querySelector('.look-nav-next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.navigatePrev(block));
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.navigateNext(block));
    }
    
    this.currentOutfitIndex = 0;
    this.updateNavButtons(block);
    
    // Setup Quick View buttons
    this.setupQuickView(block);
    
    // (Add to Cart buttons removed — Quick View modal handles ATC)
  }

  // Inject ol-qa-data scripts so the shared quick-add-modal-olevorr can read product data.
  // The shared modal listens for .ol-pc-qv clicks via event delegation on the parent section.
  setupQuickView(block) {
    this._injectProductData(block);
  }

  /* Inject ol-qa-data scripts for each product in this outfit group,
     so the shared quick-add-modal-olevorr snippet can read them on click. */
  _injectProductData(block) {
    var products = this.outfitBundles ? [].concat(...this.outfitBundles) : [];
    var seen = new Set();
    products.forEach(function (p) {
      if (!p || p.isPlaceholder || !p.handle || seen.has(p.id)) return;
      seen.add(p.id);
      if (document.querySelector('.ol-qa-data[data-product-id="' + p.id + '"]')) return;

      fetch('/products/' + p.handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          /* Match the exact ol-qa-data format from card-product-olevorr.liquid */
          var rawImg = product.featured_image || (product.images && product.images[0]) || '';

          /* Resize to 800px — same as Liquid's | image_url: width: 800 */
          function sizedImg(url) {
            if (!url) return '';
            return url.replace(/\.(jpg|jpeg|png|webp|gif)/i, '_800x.$1');
          }

          var featImg = sizedImg(rawImg);

          /* Reconstruct options_with_values (same structure as Liquid's product.options_with_values) */
          var opts = product.options.map(function (optName, idx) {
            var vals = [];
            (product.variants || []).forEach(function (v) {
              var val = v['option' + (idx + 1)];
              if (val && vals.indexOf(val) < 0) vals.push(val);
            });
            return { name: optName, values: vals };
          });

          /* Variant images also sized to 800px */
          var vars = (product.variants || []).map(function (v) {
            var vi = v.featured_image ? sizedImg(v.featured_image.src) : featImg;
            return { id: v.id, title: v.title, option1: v.option1, option2: v.option2, available: v.available, image: vi };
          });

          /* Price — match Liquid's {{ price | money }} output */
          var fmtPrice = typeof Shopify !== 'undefined' && Shopify.formatMoney
            ? Shopify.formatMoney(product.price)
            : '₹ ' + (product.price / 100).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2});

          var ds = document.createElement('script');
          ds.type = 'application/json';
          ds.className = 'ol-qa-data';
          ds.setAttribute('data-product-id', product.id);
          ds.textContent = JSON.stringify({
            id: product.id,
            url: '/products/' + product.handle,
            title: product.title,
            price: fmtPrice,
            image: featImg,
            options: opts,
            variants: vars
          });
          block.appendChild(ds);
        })
        .catch(function () {});
    });
  }


  navigatePrev(block) {
    if (this.currentOutfitIndex > 0) {
      this.currentOutfitIndex--;
      this.showOutfitGroup(this.currentOutfitIndex);
      this.updateNavButtons(block);
      this.updateCurrentPage(block);
    }
  }

  navigateNext(block) {
    if (this.currentOutfitIndex < this.totalOutfits - 1) {
      this.currentOutfitIndex++;
      this.showOutfitGroup(this.currentOutfitIndex);
      this.updateNavButtons(block);
      this.updateCurrentPage(block);
    }
  }

  updateCurrentPage(block) {
    const currentPageEl = block.querySelector('.current-page');
    if (currentPageEl) {
      currentPageEl.textContent = (this.currentOutfitIndex + 1).toString();
    }
  }

  updateNavButtons(block) {
    const prevBtn = block.querySelector('.look-nav-prev');
    const nextBtn = block.querySelector('.look-nav-next');
    
    if (prevBtn) {
      prevBtn.disabled = this.currentOutfitIndex === 0;
    }
    
    if (nextBtn) {
      nextBtn.disabled = this.currentOutfitIndex >= (this.totalOutfits - 1);
    }
  }

  hideBlock(block) {
    block.style.display = 'none';
  }

  getShopifyPrice(product) {
    if (!product) return 0;
    
    // Get price from variants (most reliable - directly from Shopify API)
    let price = 0;
    if (product.variants && product.variants.length > 0) {
      price = product.variants[0].price;
    } else if (product.price) {
      price = product.price;
    } else if (product.price_min) {
      price = product.price_min;
    }
    
    // Handle string prices
    if (typeof price === 'string') {
      price = parseFloat(price) || 0;
    }
    
    // Smart detection: Check if price is in cents or rupees
    // Shopify API can return prices in different formats depending on source
    let finalPrice = price;
    
    // If price is very large (>= 10000), it's likely in cents/paise
    // Example: 204800 paise = ₹2048
    if (price >= 10000) {
      finalPrice = price / 100;
      if (this.debug) {
        console.log('💰 Price converted from cents:', {
          product: product.title,
          rawPrice: price,
          convertedPrice: finalPrice,
          reason: 'Price >= 10000 (likely in cents)'
        });
      }
    } 
    // If price is between 100-9999, check if it's a round number (likely cents)
    // Example: 2000 could be ₹2000 OR 2000 paise (₹20)
    // We check: if it ends in 00 and is > 1000, it's likely cents
    else if (price >= 1000 && price % 100 === 0) {
      // This is ambiguous - could be ₹2000 or 2000 paise
      // Check if there are other indicators
      // For now, assume it's in rupees if < 10000
      finalPrice = price;
      if (this.debug) {
        console.log('💰 Price kept as-is (ambiguous):', {
          product: product.title,
          rawPrice: price,
          finalPrice: finalPrice,
          reason: 'Between 1000-9999, assuming rupees'
        });
      }
    }
    // If price is < 1000, it's definitely in rupees
    else {
      finalPrice = price;
      if (this.debug) {
        console.log('💰 Price kept as-is (small value):', {
          product: product.title,
          rawPrice: price,
          finalPrice: finalPrice,
          reason: 'Price < 1000 (definitely rupees)'
        });
      }
    }
    
    return Math.max(0, finalPrice || 0);
  }

  getShopifyImage(product) {
    if (!product) return '';
    let imageUrl = product.featured_image || (product.images && product.images[0]) || product.image || '';
    
    if (typeof imageUrl === 'object' && imageUrl.src) {
      imageUrl = imageUrl.src;
    }
    
    imageUrl = String(imageUrl || '').trim();
    if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') return '';
    
    if (!imageUrl.startsWith('http')) {
      if (imageUrl.startsWith('//')) {
        imageUrl = 'https:' + imageUrl;
      } else if (imageUrl.startsWith('/')) {
        imageUrl = window.location.origin + imageUrl;
      } else {
        imageUrl = window.location.origin + '/' + imageUrl;
      }
    }
    
    return imageUrl;
  }

  formatSimplePrice(price) {
    if (!price || price === 0) return '0.00';
    const numPrice = parseFloat(price) || 0;
    return numPrice.toFixed(2);
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Cart functionality
  async addLookToCart(lookIndex) {
    try {
      if (!this.lookRecommendations || !this.lookRecommendations[lookIndex]) {
        throw new Error('Look not found');
      }

      const look = this.lookRecommendations[lookIndex];
      const button = document.querySelector(`[data-look-index="${lookIndex}"] .look-add-btn`);
      
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding...';
      }

      const itemsToAdd = [];
      
      // Add primary product
      if (look.primary) {
        const validation = this.validateProductForCart(look.primary);
        
        if (!validation.valid) {
          throw new Error(`Cannot add ${look.primary.title}: ${validation.reason}`);
        }
        
        let primaryVariantId = null;
        if (look.primary.variants && look.primary.variants.length > 0) {
          const primaryVariant = look.primary.variants.find(v => v.available && v.id) || 
                                 look.primary.variants.find(v => v.id) || 
                                 look.primary.variants[0];
          primaryVariantId = primaryVariant.id || primaryVariant.variant_id;
        } else if (look.primary.id) {
          primaryVariantId = look.primary.id;
        }
        
        if (primaryVariantId && !isNaN(primaryVariantId)) {
          itemsToAdd.push({
            id: parseInt(primaryVariantId),
            quantity: 1,
            title: look.primary.title
          });
        } else {
          throw new Error(`Cannot add ${look.primary.title} - no valid variant ID`);
        }
      }

      if (itemsToAdd.length === 0) {
        throw new Error('No valid items to add to cart');
      }

      const result = await this.addMultipleItemsToCart(itemsToAdd);
      
      if (button) {
        button.textContent = 'Added! ✓';
        button.classList.add('btn-success');
        
        setTimeout(() => {
          button.disabled = false;
          button.textContent = 'Add to Cart';
          button.classList.remove('btn-success');
        }, 2000);
      }

      this.showCartNotification(result);
      
    } catch (error) {
      console.error('Failed to add look to cart:', error);
      
      const button = document.querySelector(`[data-look-index="${lookIndex}"] .look-add-btn`);
      if (button) {
        button.disabled = false;
        button.textContent = 'Add to Cart';
      }
      
      this.showErrorNotification(error.message || 'Failed to add item to cart');
    }
  }

  async addMultipleItemsToCart(items) {
    if (!items || items.length === 0) {
      throw new Error('No items to add to cart');
    }

    let addedCount = 0;
    const results = [];
    const errors = [];

    for (const item of items) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: item.id,
            quantity: item.quantity || 1
          })
        });

        if (response.ok) {
          const result = await response.json();
          results.push(result);
          addedCount++;
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMsg = errorData.message || `HTTP ${response.status}`;
          errors.push(`Failed to add ${item.title}: ${errorMsg}`);
        }
      } catch (error) {
        errors.push(`Failed to add ${item.title}: ${error.message}`);
      }
    }

    if (addedCount > 0) {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:items-added', {
        detail: { items: results, addedCount: addedCount }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    if (addedCount === 0) {
      throw new Error(`Failed to add items: ${errors.join(', ')}`);
    }

    return {
      addedCount: addedCount,
      totalItems: items.length,
      results: results,
      errors: errors
    };
  }

  showCartNotification(result) {
    const lastItem = result && result.results && result.results.length
      ? result.results[result.results.length - 1]
      : null;

    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && cartNotification.renderContents && lastItem) {
      cartNotification.renderContents(lastItem);
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      cartDrawer.open();
      return;
    }

    const itemCount = result ? result.addedCount : 0;
    this.showSimpleNotification(`Added ${itemCount} item${itemCount > 1 ? 's' : ''} to cart!`, 'success');
  }

  showErrorNotification(message) {
    this.showSimpleNotification(message, 'error');
  }

  showSimpleNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `simple-notification simple-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#dc3545' : type === 'success' ? '#28a745' : '#007bff'};
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Initialize
window.CompleteLookBlock = new CompleteLookBlock();

// Make addLookToCart available globally
window.addLookToCart = function(lookIndex) {
  if (window.CompleteLookBlock) {
    window.CompleteLookBlock.addLookToCart(lookIndex);
  }
};

// Extend CompleteLookBlock with helper utilities
if (window.CompleteLookBlock) {
  /* setupAddToCart / showSizeSelector / createSizeModal / addModalStyles / addToCartDirect removed.
     Quick View modal now handles all add-to-cart flows via the class methods _initCtlModal / _openCtlModal / _ctlAddToCart. */

  /*  OLD CODE REMOVED — START (setupAddToCart / showSizeSelector / createSizeModal / addModalStyles / addToCartDirect)
      Branded modal (_initCtlModal / _openCtlModal / _ctlAddToCart) handles everything now.
    try {
      // Fetch product data to get variants
      const productData = await this.fetchProductData(productHandle);
      
      if (!productData || !productData.variants || productData.variants.length === 0) {
        throw new Error('Product variants not found');
      }
      
      // Create and show size selector modal
      this.createSizeModal(productData, buttonElement, restoreCallback);
      
    } catch (error) {
      console.error('Failed to show size selector:', error);
      this.showErrorNotification('Failed to load product options');
      restoreCallback();
    }
  };

  // Create size selector modal
  window.CompleteLookBlock.createSizeModal = function(product, buttonElement, restoreCallback) {
    // Remove existing modal if present
    const existingModal = document.getElementById('btl-size-modal');
    if (existingModal) {
      existingModal.remove();
    }
    
    // Create modal HTML
    const modal = document.createElement('div');
    modal.id = 'btl-size-modal';
    modal.className = 'btl-size-modal';
    modal.innerHTML = `
      <div class="btl-size-modal__backdrop"></div>
      <div class="btl-size-modal__content">
        <div class="btl-size-modal__header">
          <h3 class="btl-size-modal__title">Select Size</h3>
          <button class="btl-size-modal__close" aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="btl-size-modal__body">
          <div class="btl-size-modal__product">
            <img src="${this.getShopifyImage(product)}" alt="${this.escapeHtml(product.title)}" class="btl-size-modal__image">
            <div class="btl-size-modal__details">
              <h4 class="btl-size-modal__product-title">${this.escapeHtml(product.title)}</h4>
              <div class="btl-size-modal__price">₹${this.formatPrice(this.getShopifyPrice(product))}</div>
            </div>
          </div>
          <div class="btl-size-modal__variants">
            ${this.createVariantOptions(product)}
          </div>
          <div class="btl-size-modal__actions">
            <button class="btl-size-modal__add-btn" disabled>Add to Cart</button>
          </div>
        </div>
      </div>
    `;
    
    // Add modal styles
    this.addModalStyles();
    
    // Add to DOM
    document.body.appendChild(modal);
    
    // Setup modal event handlers
    this.setupModalEvents(modal, product, buttonElement, restoreCallback);
    
    // Show modal
    setTimeout(() => {
      modal.classList.add('btl-size-modal--active');
    }, 10);
  };

  // Create variant option buttons
  window.CompleteLookBlock.createVariantOptions = function(product) {
    const availableVariants = product.variants.filter(v => v.available);
    
    if (availableVariants.length === 0) {
      return '<div class="btl-size-modal__no-variants">No sizes available</div>';
    }
    
    return availableVariants.map(variant => `
      <button class="btl-size-modal__variant-btn" 
              data-variant-id="${variant.id}"
              data-variant-title="${this.escapeHtml(variant.title)}">
        ${this.escapeHtml(variant.title)}
      </button>
    `).join('');
  };

  // Setup modal event handlers
  window.CompleteLookBlock.setupModalEvents = function(modal, product, buttonElement, restoreCallback) {
    const closeBtn = modal.querySelector('.btl-size-modal__close');
    const backdrop = modal.querySelector('.btl-size-modal__backdrop');
    const addBtn = modal.querySelector('.btl-size-modal__add-btn');
    const variantBtns = modal.querySelectorAll('.btl-size-modal__variant-btn');
    
    let selectedVariantId = null;
    
    // Close modal handlers
    const closeModal = () => {
      modal.classList.remove('btl-size-modal--active');
      setTimeout(() => {
        modal.remove();
        restoreCallback();
      }, 300);
    };
    
    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);
    
    // Escape key to close
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
    
    // Variant selection
    variantBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        // Remove active state from all buttons
        variantBtns.forEach(b => b.classList.remove('btl-size-modal__variant-btn--active'));
        
        // Add active state to clicked button
        btn.classList.add('btl-size-modal__variant-btn--active');
        
        // Enable add button
        selectedVariantId = btn.dataset.variantId;
        addBtn.disabled = false;
      });
    });
    
    // Add to cart handler
    addBtn.addEventListener('click', async () => {
      if (!selectedVariantId) return;
      
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';
      
      try {
        await this.addToCartDirect(selectedVariantId, buttonElement, () => {});
        closeModal();
      } catch (error) {
        addBtn.disabled = false;
        addBtn.textContent = 'Add to Cart';
        this.showErrorNotification('Failed to add item to cart');
      }
    });
  };

  // Add modal styles to page
  window.CompleteLookBlock.addModalStyles = function() {
    if (document.getElementById('btl-modal-styles')) return;
    
    const styles = document.createElement('style');
    styles.id = 'btl-modal-styles';
    styles.textContent = `
      .btl-size-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      
      .btl-size-modal--active {
        opacity: 1;
        visibility: visible;
      }
      
      .btl-size-modal__backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
      }
      
      .btl-size-modal__content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border-radius: 8px;
        max-width: 500px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      }
      
      .btl-size-modal__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1.5rem;
        border-bottom: 1px solid #eee;
      }
      
      .btl-size-modal__title {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 500;
      }
      
      .btl-size-modal__close {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 4px;
        transition: background 0.2s ease;
      }
      
      .btl-size-modal__close:hover {
        background: #f5f5f5;
      }
      
      .btl-size-modal__body {
        padding: 1.5rem;
      }
      
      .btl-size-modal__product {
        display: flex;
        gap: 1rem;
        margin-bottom: 1.5rem;
      }
      
      .btl-size-modal__image {
        width: 80px;
        height: 100px;
        object-fit: cover;
        border-radius: 4px;
      }
      
      .btl-size-modal__details {
        flex: 1;
      }
      
      .btl-size-modal__product-title {
        margin: 0 0 0.5rem 0;
        font-size: 1rem;
        font-weight: 500;
      }
      
      .btl-size-modal__price {
        font-size: 0.9rem;
        color: #666;
      }
      
      .btl-size-modal__variants {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
        gap: 0.75rem;
        margin-bottom: 1.5rem;
      }
      
      .btl-size-modal__variant-btn {
        background: white;
        border: 1px solid #ddd;
        padding: 0.75rem;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.9rem;
      }
      
      .btl-size-modal__variant-btn:hover {
        border-color: #999;
      }
      
      .btl-size-modal__variant-btn--active {
        background: #000;
        color: white;
        border-color: #000;
      }
      
      .btl-size-modal__actions {
        text-align: center;
      }
      
      .btl-size-modal__add-btn {
        background: #000;
        color: white;
        border: none;
        padding: 0.75rem 2rem;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9rem;
        transition: opacity 0.2s ease;
      }
      
      .btl-size-modal__add-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .btl-size-modal__no-variants {
        text-align: center;
        color: #666;
        font-style: italic;
        padding: 1rem;
      }
      
      .look-add-to-cart-btn {
        background: transparent;
        border: 1px solid rgba(var(--color-foreground), 0.15);
        color: rgb(var(--color-foreground));
        font-size: 0.75rem;
        font-weight: 400;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        cursor: pointer;
        transition: all 0.2s ease;
        border-radius: 2px;
        padding: 0.6rem 1.2rem;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        margin-right: 0.5rem;
      }
      
      .look-add-to-cart-btn:hover {
        background: rgba(var(--color-foreground), 0.03);
        border-color: rgba(var(--color-foreground), 0.25);
      }
      
      .look-add-to-cart-btn--success {
        background: #28a745;
        border-color: #28a745;
        color: white;
      }
    `;
    
    document.head.appendChild(styles);
  };

  // Add product directly to cart (no variants)
  window.CompleteLookBlock.addToCartDirect = async function(variantId, buttonElement, restoreCallback) {
    try {
      if (!variantId) {
        throw new Error('No variant ID provided');
      }
      
      // Use global CartManager if available, otherwise use fetch directly
      if (window.cartManager) {
        await window.cartManager.addToCart(variantId, 1);
      } else {
        // Fallback to direct fetch
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }
        
        // Trigger cart update events
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
      
      // Show success state
      buttonElement.innerHTML = 'Added! ✓';
      buttonElement.classList.add('look-add-to-cart-btn--success');
      
      // Show success notification
      this.showSuccessNotification('Item added to cart!');
      
      // Restore button after delay
      setTimeout(() => {
        restoreCallback();
        buttonElement.classList.remove('look-add-to-cart-btn--success');
      }, 2000);
      
    } catch (error) {
      console.error('Failed to add to cart:', error);
  OLD CODE REMOVED — END */

  // Check if product has multiple variants (size options)
  window.CompleteLookBlock.hasVariants = function(product) {
    if (!product || !product.variants) return false;
    
    // If product has more than one variant, or if the single variant has options other than "Default Title"
    if (product.variants.length > 1) return true;
    
    if (product.variants.length === 1) {
      const variant = product.variants[0];
      return variant.title !== 'Default Title' && 
             (variant.option1 || variant.option2 || variant.option3);
    }
    
    return false;
  };

  // Get default variant ID for products without size options
  window.CompleteLookBlock.getDefaultVariantId = function(product) {
    if (!product || !product.variants || product.variants.length === 0) return null;
    
    // Return the first available variant ID
    const availableVariant = product.variants.find(v => v.available && v.id) || product.variants[0];
    return availableVariant ? availableVariant.id : null;
  };
}
